import type { posthog } from 'posthog-js';

type PostHogInitConfig = NonNullable<Parameters<typeof posthog.init>[1]>;

/** The ATS widgets (Bullhorn, Bullhorn for Salesforce, MSF) run inside another system's page. */
export const isWidgetPath = (pathname: string) => /^\/(BhWidget|BhrcWidget|MsfWidget)(\/|$)/.test(pathname);

/**
 * PostHog settings for the page being loaded.
 *
 * - Recruiter app: consent-based. Nothing is captured or stored until the recruiter clicks
 *   "Accept" (see consent.ts). Masked recordings start only after consent.
 * - ATS widgets (Bullhorn, Bullhorn for Salesforce, MSF): no analytics. They run inside another
 *   system's page and are left out for now; nothing is stored and every event is dropped. Feature
 *   flags still work.
 */
export const buildPosthogConfig = (
  base: { api_host: string; ui_host: string },
  pathname: string,
  beforeSend: PostHogInitConfig['before_send'],
): PostHogInitConfig => {
  const shared: PostHogInitConfig = {
    ...base,
    defaults: '2026-01-30',
    before_send: beforeSend,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_exceptions: false,
    // Clicks, heatmaps, rage and dead clicks, with all text and attributes masked.
    autocapture: { dom_event_allowlist: ['click', 'change', 'submit'] },
    mask_all_text: true,
    mask_all_element_attributes: true,
    rageclick: true,
    capture_dead_clicks: true,
    enable_heatmaps: true,
    // Page speed (web vitals). Network timing in recordings stays off.
    capture_performance: { web_vitals: true, network_timing: false },
    disable_session_recording: true,
  };

  if (isWidgetPath(pathname)) {
    // Left out of analytics for now: nothing stored, and every event dropped.
    return {
      ...shared,
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      person_profiles: 'never',
      before_send: () => null,
    };
  }

  return {
    ...shared,
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    person_profiles: 'identified_only',
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: 'img, video, iframe, canvas, object, embed',
    },
  };
};
